import React, {Component} from "react";
import {withRouter} from "react-router-dom";
import PropTypes from "prop-types";
import compose from "lodash/flowRight";
import {graphql, withApollo} from "react-apollo";
import {connect} from "react-redux";
import concat from "lodash/concat";
import get from "lodash/get";
import {withTranslation} from "@unibuddy/intl";
import withAnalytics from "../../../AnalyticsNew/withAnalytics/withAnalytics";
import RecordAnalytics from "../../../Analytics/RecordAnalytics";
import withAuth from "../../../Auth/withAuth/withAuth";
import {rootURL} from "../../../Utils/WhereAmI";
import sendMessageMutation from "../../../Chat/mutations/sendMessageMutation";
import chatGroupQuery from "../../../Chat/queries/chatGroupQuery";
import messagesQuery from "../../../Chat/queries/messagesQuery";
import getOrCreateChatGroupQuery from "../../../Inbox/queries/getOrCreateChatGroupQuery";
import addBotToChatGroupMutation from "../../../Chat/mutations/addBotToChatGroupMutation";
import mentorIntroMessageQuery from "../../../Chat/queries/mentorIntroMessageQuery";
import * as actions from "../../../Register/actions/firstMessageActions";
import {isFeatureEnabled} from "../../../Utils/FeatureUtils";
import translateIntroMessage from "../../utils/translateIntroMessage";
import {getenv} from "../../../../util/getEnv";
import {IntlNamespaces} from "../../../../translations/types";

export function withStartChatComponent() {
    return function withStartChatComponentWrapped(WrappedComponent) {
        class withStartChatContainer extends Component {
            state = {
                messagesByOpponentId: {},
            };

            componentDidMount() {
                RecordAnalytics.page(this.props, "Start Chat");
                const chatGroupId = get(this.props, "chatGroup.id");
                const search = get(this.props, "location.search", "");
                if (chatGroupId) {
                    const rootUrl = rootURL(this.props.location.pathname);
                    this.props.history.replace(`${rootUrl}/inbox/${chatGroupId}${search}`);
                }
            }

            componentWillReceiveProps(newProps) {
                const newChatGroupId = get(newProps, "chatGroup.id");
                const previousChatGroupId = get(this.props, "chatGroup.id");
                const search = get(this.props, "location.search", "");
                if (newChatGroupId && newChatGroupId !== previousChatGroupId) {
                    const rootUrl = rootURL(this.props.location.pathname);
                    this.props.history.replace(`${rootUrl}/inbox/${newChatGroupId}${search}`);
                }
                if (newProps.firstMessages !== this.props.firstMessages) {
                    // send the first message if it has been saved before the sign up
                    const text = get(
                        this.props.firstMessages,
                        `${this.props.match.params.opponentId}`,
                        "",
                    );
                    if (text !== "") {
                        this.onSendMessage(text);
                        // clear the message after sending
                        this.props.dispatch(
                            actions.setFirstMessage(this.props.match.params.opponentId, ""),
                        );
                    }
                }
            }

            async onSendMessage(text) {
                if (text === "") return;
                const opponentId = this.props.match.params.opponentId;
                let messages = [];
                const newMessage = {
                    id: Date.now(),
                    created: new Date().toISOString(),
                    text,
                    sender: {
                        id: this.props.me.anyUser.id,
                        name: "Me",
                        profilePhoto: "",
                    },
                };
                if (opponentId in this.state.messagesByOpponentId) {
                    messages = concat(this.state.messagesByOpponentId[opponentId], newMessage);
                } else {
                    messages.push(newMessage);
                }
                await this.setState({
                    messagesByOpponentId: {
                        ...this.state.messagesByOpponentId,
                        [opponentId]: messages,
                    },
                });

                const isDegreeTypesEnabled =
                    isFeatureEnabled("enableDegreeTypes", this.props.university) &&
                    !this.props.isExternal;

                const variables = {
                    opponentId: this.props.match.params.opponentId,
                    universitySlug: this.props.match.params.universitySlug,
                    isDegreeTypesEnabled,
                };

                const result = await this.props.client.query({
                    query: getOrCreateChatGroupQuery,
                    variables,
                });

                const sendMessageVariables = {
                    text,
                    chatGroupId: result.data.getOrCreateChatGroup.id,
                };

                const sendMessageResult = await this.props.sendMessage({
                    variables: sendMessageVariables,
                });
                let opponentType = get(result, "data.getOrCreateChatGroup.opponent.accountRole");
                if (
                    opponentType === "mentor" &&
                    get(result, "data.getOrCreateChatGroup.opponent.degreeLevel") === "Alumni"
                ) {
                    opponentType = "alumni";
                }
                if (get(this.props, "analytics.events.messageSent")) {
                    await this.props.analytics.events.messageSent({
                        conversationId: get(result, "data.getOrCreateChatGroup.id"),
                        opponentId: get(this.props, "match.params.opponentId"),
                        opponentType, // TO DO: Replace with group once mentor moved to 3 layer
                        chatVersion: "Version 2",
                    });
                }

                // prefetch message
                await this.props.client.query({
                    query: messagesQuery,
                    variables: {
                        chatGroupId: result.data.getOrCreateChatGroup.id,
                        isDegreeTypesEnabled,
                    },
                });
                const root = rootURL(this.props.location.pathname);
                const url = `${root}/inbox/${get(result, "data.getOrCreateChatGroup.id")}`;
                this.props.history.replace(url);

                RecordAnalytics.event(this.props, "Send Chat Message", {
                    chatGroupId: get(result, "data.getOrCreateChatGroup.id"),
                    senderId: this.props.me.anyUser.id,
                });
                return sendMessageResult;
            }

            render() {
                const DEFAULT_PROFILE_PHOTO = getenv("DEFAULT_PROFILE_PHOTO");
                const avatar =
                    get(this.props, "introMessage.sender.profilePhoto", null) ||
                    DEFAULT_PROFILE_PHOTO;
                const introMessages = this.props.introMessage
                    ? [translateIntroMessage(this.props.introMessage, this.props.t)]
                    : [];
                const opponentId = get(this.props.match.params, "opponentId");
                let messages = [];
                if (opponentId in this.state.messagesByOpponentId) {
                    messages = concat(
                        introMessages,
                        get(this.state.messagesByOpponentId, opponentId),
                    );
                } else {
                    messages = introMessages;
                }
                return (
                    <WrappedComponent
                        onSendMessage={this.onSendMessage.bind(this)}
                        messages={messages}
                        loading={this.state.loading}
                        avatar={avatar}
                        redirectUrl={this.state.redirectUrl}
                        {...this.props}
                    />
                );
            }
        }

        withStartChatContainer.contextTypes = {
            router: PropTypes.object,
        };
        return withStartChatContainer;
    };
}

const withStartChat = (app = "jigglypuff") =>
    function withStartChatWrapped(WrappedComponent) {
        const mentorIntroMessageQueryOptions = {
            name: "mentorIntroMessageQuery",
            options(props) {
                const isDegreeTypesEnabled = isFeatureEnabled(
                    "enableDegreeTypes",
                    props.university,
                );
                return {
                    variables: {
                        mentorId: props.match.params.opponentId,
                        universitySlug: props.match.params.universitySlug,
                        isDegreeTypesEnabled,
                    },
                    fetchPolicy: "cache-and-network",
                };
            },
            props({mentorIntroMessageQuery: {loading, mentorIntroMessage, error}}) {
                return {
                    introMessage: get(mentorIntroMessage, "introMessage"),
                    introLoading: loading,
                    mentorIntroMessageError: error,
                };
            },
        };

        const chatGroupQueryOptions = {
            name: "chatGroupQuery",
            options(props) {
                const isDegreeTypesEnabled = isFeatureEnabled(
                    "enableDegreeTypes",
                    props.university,
                );
                return {
                    variables: {
                        opponentId: props.match.params.opponentId,
                        universitySlug: props.match.params.universitySlug,
                        isDegreeTypesEnabled,
                    },
                    fetchPolicy: "cache-and-network",
                };
            },
            props({chatGroupQuery: {loading, chatGroup, error}}) {
                return {
                    chatGroup,
                    chatGroupLoading: loading,
                    chatGroupQueryError: error,
                };
            },
        };

        const mapStateToProps = (state) => ({
            firstMessages: get(state, "firstMessageState.firstMessages", []),
        });

        const component = withStartChatComponent(app)(WrappedComponent);
        return compose(
            withTranslation([IntlNamespaces.LEVELS, "mentorCard"]),
            withRouter,
            connect(mapStateToProps),
            withAuth,
            withApollo,
            withAnalytics,
            graphql(addBotToChatGroupMutation, {name: "addBotToChatGroup"}),
            graphql(chatGroupQuery, chatGroupQueryOptions),
            graphql(mentorIntroMessageQuery, mentorIntroMessageQueryOptions),
            graphql(sendMessageMutation, {name: "sendMessage"}),
        )(component);
    };

export default withStartChat;
